import React, { useState, useEffect, useContext } from 'react'
import { BuildingOfficeIcon, PencilIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { GlobalContext } from '../../context/GlobalContext'
import haxios from '../../lib/haxios'
import { successToast, errorToast } from '../../lib/helpers'

// Function to format Saturday and Sunday trade values
const formatTrade = (value) => {
  if (value === 0) return 'Closed'
  if (value === 0.5) return 'Half Day'
  return 'Full Day'
}

// Modal component for editing and adding branches
const BranchModal = ({ isOpen, onClose, onSubmit, branchData }) => {
  const [branch, setBranch] = useState(
    branchData || {
      Branch: '',
      MamCode: '',
      PCode: '',
      SalesTarget: '',
      MarginTarget: '',
      DailySalesTarget: '',
      MonthlySalesTarget: '',
      SaturdayTrade: '0', // Set initial value as string '0'
      SundayTrade: '0' // Set initial value as string '0'
    }
  )

  useEffect(() => {
    if (branchData) {
      setBranch({
      ...branchData,
      DailySalesTarget: branchData.DailySalesTarget || '',
      MonthlySalesTarget: branchData.MonthlySalesTarget || '',
      SaturdayTrade: String(branchData.SaturdayTrade || '0'), // Ensure the values are strings
      SundayTrade: String(branchData.SundayTrade || '0') // Ensure the values are strings
      })
    }
  }, [branchData])

  const handleChange = (e) => {
    const { name, value } = e.target
    setBranch({
      ...branch,
      [name]: value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      ...branch,
      SalesTarget: parseFloat(branch.SalesTarget), // Ensure numeric values
      MarginTarget: parseFloat(branch.MarginTarget), // Ensure numeric values
      DailySalesTarget: parseFloat(branch.DailySalesTarget) || null, // Ensure numeric values
      MonthlySalesTarget: parseFloat(branch.MonthlySalesTarget) || null, // Ensure numeric values
      SaturdayTrade: parseFloat(branch.SaturdayTrade), // Ensure numeric values
      SundayTrade: parseFloat(branch.SundayTrade) // Ensure numeric values
    })
  }

  return (
    isOpen && (
      <div className="flex fixed inset-0 z-50 justify-center items-center bg-gray-800 bg-opacity-50">
        <div className="p-6 w-1/2 bg-white rounded-lg shadow-lg">
          <h2 className="mb-4 text-2xl font-bold">{branchData ? 'Edit Branch' : 'Add New Branch'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700">Branch Name</label>
              <input
                type="text"
                name="Branch"
                value={branch.Branch}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">MamCode</label>
              <input
                type="text"
                name="MamCode"
                value={branch.MamCode}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Postcode</label>
              <input
                type="text"
                name="PCode"
                value={branch.PCode}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Sales Target (£)</label>
              <input
                type="number"
                name="SalesTarget"
                value={branch.SalesTarget}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Margin Target (%)</label>
              <input
                type="number"
                name="MarginTarget"
                value={branch.MarginTarget}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Daily Sales Target (£)</label>
              <input
                type="number"
                name="DailySalesTarget"
                value={branch.DailySalesTarget}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
                min="0"
                step="0.01"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Monthly Sales Target (£)</label>
              <input
                type="number"
                name="MonthlySalesTarget"
                value={branch.MonthlySalesTarget}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
                min="0"
                step="0.01"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Saturday Trade</label>
              <select
                name="SaturdayTrade"
                value={branch.SaturdayTrade}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
              >
                <option value="0">Closed</option>
                <option value="0.5">Half Day</option>
                <option value="1">Full Day</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Sunday Trade</label>
              <select
                name="SundayTrade"
                value={branch.SundayTrade}
                onChange={handleChange}
                className="px-3 py-2 w-full rounded-lg border"
              >
                <option value="0">Closed</option>
                <option value="0.5">Half Day</option>
                <option value="1">Full Day</option>
              </select>
            </div>
            <div className="flex justify-end space-x-2">
              <button type="button" className="px-4 py-2 bg-gray-300 rounded-lg" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 text-white bg-blue-500 rounded-lg">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  )
}

// BranchCard Component
const BranchCard = ({ branch, onEdit, onDelete }) => {
  const navigate = useNavigate()

  const handleViewBranch = (e) => {
    e.stopPropagation()
    navigate(`/branch/${branch.MamCode}`)
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 cursor-pointer" onClick={handleViewBranch}>
            <div className="p-3 rounded-lg bg-indigo-100">
              <BuildingOfficeIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 hover:text-indigo-600 transition-colors">{branch.Branch}</h3>
              <p className="text-sm text-gray-500 mt-1">{branch.PCode}</p>
            </div>
            <ArrowRightIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="flex space-x-2 ml-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEdit(branch)
              }}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit branch"
            >
              <PencilIcon className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDelete(branch._id)
              }}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete branch"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">MamCode</div>
            <div className="text-sm font-semibold text-gray-900">{branch.MamCode}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Sales Target</div>
            <div className="text-sm font-semibold text-gray-900">£{parseInt(branch.SalesTarget || 0).toLocaleString('en-GB')}</div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Margin Target</div>
            <div className="text-sm font-semibold text-gray-900">{branch.MarginTarget}%</div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Daily Target</div>
            <div className="text-sm font-semibold text-gray-900">
              {branch.DailySalesTarget ? `£${parseInt(branch.DailySalesTarget).toLocaleString('en-GB')}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Monthly Target</div>
            <div className="text-sm font-semibold text-gray-900">
              {branch.MonthlySalesTarget ? `£${parseInt(branch.MonthlySalesTarget).toLocaleString('en-GB')}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Weekend Trade</div>
            <div className="text-sm font-semibold text-gray-900">
              Sat: {formatTrade(branch.SaturdayTrade)}, Sun: {formatTrade(branch.SundayTrade)}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={handleViewBranch}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <span className="font-medium">View Branch Page</span>
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// Main Branch Component
function Branch() {
  const [branches, setBranches] = useState([])
  const [filteredBranches, setFilteredBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentBranch, setCurrentBranch] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const { cinfo, apir, token } = useContext(GlobalContext)

  useEffect(() => {
    fetchBranches()
  }, [])

  async function fetchBranches() {
    try {
      const response = await fetch(`${apir}/salesassist/cplusassist/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      console.log('API Response Status:', response.status) // Log the response status
      if (!response.ok) {
        throw new Error(`Failed to fetch branches. Status: ${response.status}`)
      }

      const data = await response.json()
      console.log('Branches Data:', data) // Log the response data
      setBranches(data)
      setFilteredBranches(data) // Initialize the filteredBranches with all branches
    } catch (error) {
      console.error('Error Fetching Branches:', error)
      setError('Failed to fetch branches.')
    } finally {
      setLoading(false)
    }
  }

  // Handle search input
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase()
    setSearchQuery(query)
    const filtered = branches.filter((branch) => branch.Branch.toLowerCase().includes(query))
    setFilteredBranches(filtered)
  }

  const handleEdit = (branch) => {
    setCurrentBranch(branch)
    setIsModalOpen(true)
  }

  const handleAddNew = () => {
    setCurrentBranch(null)
    setIsModalOpen(true)
  }

  const handleSaveBranch = async (branch) => {
    // Ensure numeric values for all fields
    const preparedBranch = {
      ...branch,
      SalesTarget: parseFloat(branch.SalesTarget),
      MarginTarget: parseFloat(branch.MarginTarget),
      DailySalesTarget: branch.DailySalesTarget ? parseFloat(branch.DailySalesTarget) : null,
      MonthlySalesTarget: branch.MonthlySalesTarget ? parseFloat(branch.MonthlySalesTarget) : null,
      SaturdayTrade: parseFloat(branch.SaturdayTrade),
      SundayTrade: parseFloat(branch.SundayTrade)
    }
    console.log(preparedBranch)
    const method = branch._id ? 'PUT' : 'POST'
    const endpoint = branch._id
      ? `${apir}/salesassist/cplusassist/updateBranch/${branch._id}`
      : `${apir}/salesassist/cplusassist/addBranch` // Use single quotes for the POST endpoint

    try {
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(preparedBranch)
      })

      if (response.ok) {
        fetchBranches() // Refresh branches
        setIsModalOpen(false) // Close modal after saving
      } else {
        throw new Error('Failed to save branch')
      }
    } catch (error) {
      setError(error.message)
    }
  }

  const handleDelete = async (branchId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this branch?')
    if (confirmDelete) {
      try {
        const response = await fetch(`${apir}/salesassist/cplusassist/deleteBranch/${branchId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })
        if (response.ok) {
          setBranches(branches.filter((branch) => branch._id !== branchId))
          setFilteredBranches(filteredBranches.filter((branch) => branch._id !== branchId))
        } else {
          throw new Error('Failed to delete branch')
        }
      } catch (error) {
        setError('Failed to delete the branch.')
      }
    }
  }

  // Set document title
  useEffect(() => {
    document.title = 'Branch Settings - Sales Assist';
    return () => {
      document.title = 'Sales Assist';
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading branches...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="px-6 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <BuildingOfficeIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Branch Management</h1>
              <p className="text-sm text-gray-600 mt-1">Manage branch settings and configurations</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search branches..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <button
              onClick={handleAddNew}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <PlusIcon className="mr-2 w-5 h-5" />
              Add New Branch
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-6">
        <div className="px-6">
          {!loading && filteredBranches.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <BuildingOfficeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery ? 'No branches found' : 'No branches yet'}
              </h3>
              <p className="text-gray-500 mb-4">
                {searchQuery
                  ? 'Try adjusting your search criteria.'
                  : 'Get started by adding your first branch.'}
              </p>
              {!searchQuery && (
                <button
                  onClick={handleAddNew}
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <PlusIcon className="mr-2 w-5 h-5" />
                  Add New Branch
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBranches.map((branch) => (
                <BranchCard key={branch._id} branch={branch} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      </div>

      <BranchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSaveBranch}
        branchData={currentBranch}
      />
    </div>
  )
}

export default Branch
